import { useState } from "react"

import useUserSettings from "hooks/useUserSettings"
import useUpdateUserSettings from "mutations/useUpdateUserSettings"

/**
 * @param {string} key - a unique panel key, e.g. "duplicationRuleAssistant.collapsed"
 * @param {boolean} [defaultCollapsed=false]
 * @returns {[boolean, () => void, (value: boolean) => void]} [collapsed, toggle, setCollapsed]
 */
export const usePersistedPanelCollapse = (key, defaultCollapsed = false) => {
  const userSettings = useUserSettings()
  const updateUserSettings = useUpdateUserSettings()
  const panels = userSettings?.panels || {}

  // Seeded from the stored value, then owned locally. Deriving it from the store instead
  // would leave the panel unable to move until the PUT resolved and dispatched into
  // Redux — a visible pause on every toggle. Seeding once is safe because `useUser` reads
  // Redux, which auth has already populated, and callers mount this fresh each time the
  // panel appears.
  const [collapsed, setLocalCollapsed] = useState(
    key in panels ? !!panels[key] : defaultCollapsed
  )

  const setCollapsed = (value) => {
    setLocalCollapsed(value)

    updateUserSettings.mutate({
      panels: { [key]: value }
    })
  }

  const toggle = () => setCollapsed(!collapsed)

  return [collapsed, toggle, setCollapsed]
}
